"use client";

import { useState, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { loadAndApplyThemeOverrides } from "~/widgets/lib/theme";
import { BOOKING_PORTAL_IDS } from "@baluu/ui/lib/checkout/portals";

// Import the same CSS used by all widgets
// @ts-expect-error - Vite inline import
import cssText from "~/widgets/styles/globals.css?inline";

export type FilterOverlayPortalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  primaryColor?: string;
  /** Business slug for loading theme overrides */
  businessSlug?: string;
  /** API URL for loading theme overrides */
  apiUrl?: string;
};

/**
 * FilterOverlayPortal - Renders filter content in a Shadow DOM portal
 *
 * This escapes any stacking context from the parent widget and provides
 * complete CSS isolation from the host page.
 *
 * Uses the same globals.css as the rest of the widgets for consistent theming.
 */
export function FilterOverlayPortal({
  isOpen,
  onClose,
  children,
  primaryColor = "#03c2b9",
  businessSlug,
  apiUrl,
}: FilterOverlayPortalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [shadowContainer, setShadowContainer] = useState<HTMLDivElement | null>(null);

  // Create Shadow DOM container when portal opens
  useEffect(() => {
    if (isOpen && !containerRef.current) {
      // Create container div attached directly to body
      const container = document.createElement("div");
      container.id = BOOKING_PORTAL_IDS.timetableFilters;
      document.body.appendChild(container);
      containerRef.current = container;

      // Attach Shadow DOM
      const shadowRoot = container.attachShadow({ mode: "open" });
      shadowRootRef.current = shadowRoot;

      // Inject CSS - same approach as injectCssIntoShadow in widgets/index.ts
      let css =
        cssText ||
        (window as unknown as { __BALUU_WIDGETS_CSS__?: string })
          .__BALUU_WIDGETS_CSS__ ||
        "";

      if (css) {
        // Replace :root with :host so CSS variables cascade properly in Shadow DOM
        css = css.replace(/:root\s*\{/g, ":host {");

        const style = document.createElement("style");
        style.textContent = css;
        shadowRoot.appendChild(style);
      }

      // Add overlay-specific styles (positioning only - theme comes from globals.css + loadAndApplyThemeOverrides)
      const overlayStyle = document.createElement("style");
      overlayStyle.textContent = `
        :host {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999999;
          display: block;
        }
      `;
      shadowRoot.appendChild(overlayStyle);

      // Create inner container for React to render into
      // Note: Theme CSS variables come from globals.css (:host) and loadAndApplyThemeOverrides
      // Do NOT set inline styles here as they would override the theme
      const shadowContainerEl = document.createElement("div");
      shadowContainerEl.id = "shadow-content";
      shadowRoot.appendChild(shadowContainerEl);
      setShadowContainer(shadowContainerEl);

      // Load and apply theme overrides from API (same as main widget)
      if (businessSlug) {
        loadAndApplyThemeOverrides(businessSlug, shadowRoot, apiUrl).catch((err) => {
          console.warn("[FilterOverlayPortal] Failed to load theme overrides:", err);
        });
      }

      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }

    return () => {
      // Cleanup when portal closes
      if (!isOpen && containerRef.current) {
        containerRef.current.remove();
        containerRef.current = null;
        shadowRootRef.current = null;
        setShadowContainer(null);
        document.body.style.overflow = "";
      }
    };
  }, [isOpen, businessSlug, apiUrl]);

  // Theme CSS variables are handled by globals.css and loadAndApplyThemeOverrides
  // Do NOT set inline styles here as they would override the theme

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        containerRef.current.remove();
        document.body.style.overflow = "";
      }
    };
  }, []);

  // Don't render if not open or container not ready
  if (!isOpen || !shadowContainer) {
    return null;
  }

  const portalContent = (
    <div className="fixed inset-0 flex flex-col font-sans">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* Sheet container - slides from top, takes most of viewport on mobile */}
      <div className="relative bg-white rounded-b-2xl shadow-xl min-h-[70vh] max-h-[90vh] flex flex-col animate-in slide-in-from-top duration-200">
        {children}
      </div>
    </div>
  );

  // Render via portal into the Shadow DOM container
  return createPortal(portalContent, shadowContainer);
}
